/** @mui/material imports */
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
/** @mui/icons-material imports */
import SearchIcon from '@mui/icons-material/Search';
/** React imports */
import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/** App imports */
import { MenuItem, SearchGroup } from '../../Components/Sidebar';

interface FlatSearchItem {
  /**
   * The menu item object from the sidebar config, which contains all the relevant
   * information for navigation and display.
   * @example
   * {
   *   key: "merge-data",
   *   primary: "MERGE_DATA",
   *   linkTo: "/goldspot/projects/px-test2/merge-data",
   *   accessible: true
   * }
   */
  item: MenuItem;
  /**
   * Translation key suffix for the section header
   * @example
   * 'DATA_PROCESSING' for the "Data Processing" section
   */
  groupTitle: string;
  /**
   * Translated menu item label, used for display and filtering
   * @example
   * 'MERGE_DATA' for the "Merge Data" menu item
   */
  label: string;
}

interface SidebarSearchProps {
  groups: SearchGroup[];
}

const filter = createFilterOptions<FlatSearchItem>({
  stringify: (option) => option.label,
  trim: true,
  ignoreCase: true,
});

export const SidebarSearch = ({ groups }: SidebarSearchProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState<string>('');

  /**
   * Flatten all groups into a single list ordered by section, recursing into
   * subItems. Only leaf items with a real `linkTo` destination are included.
   * The order here matters for groupBy — MUI groups consecutive equal keys.
   */
  const flatItems = useMemo((): FlatSearchItem[] => {
    const result: FlatSearchItem[] = [];

    const flattenItems = (items: MenuItem[], groupTitle: string): void => {
      items.forEach((item) => {
        if (item.linkTo) {
          result.push({
            item,
            groupTitle,
            label: t(`NAVIGATION_ITEM_${item.primary}`),
          });
        }

        if (item.subItems) {
          // Group-header items (no linkTo): skip self, descend into children
          flattenItems(item.subItems, groupTitle);
        }
      });
    };

    groups.forEach(({ title, items }) => flattenItems(items, title));
    return result;
  }, [groups, t]);

  const handleChange = (value: FlatSearchItem | null) => {
    if (!value || !value.item.accessible) {
      return;
    }
    setInputValue('');
    navigate(value.item.linkTo);
  };

  return (
    <Autocomplete<FlatSearchItem>
      fullWidth
      size="small"
      options={flatItems}
      /* Group by section, keeping the original sidebar order */
      groupBy={(option) => t(`SIDEBAR_TITLE_${option.groupTitle}`)}
      getOptionLabel={(option) => option.label}
      /* Don't show any items until the user starts typing */
      filterOptions={(options, state) => {
        if (!state.inputValue.trim()) return [];
        return filter(options, state);
      }}
      getOptionDisabled={(option) => !option.item.accessible}
      isOptionEqualToValue={(option, value) =>
        option.item.key === value.item.key
      }
      /* Always a "no selection" input — value resets after navigation */
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => handleChange(value)}
      clearOnEscape
      blurOnSelect
      slotProps={{
        popper: {
          sx: { zIndex: (theme) => theme.zIndex.drawer },
        },
      }}
      noOptionsText={
        inputValue ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {t('SIDEBAR_SEARCH_NO_RESULTS')}
          </Typography>
        ) : null
      }
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 2,
              pt: 0.75,
              pb: 0.25,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'text.secondary',
              lineHeight: '20px',
            }}
          >
            {params.group}
          </Typography>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const badgeSubtext: string | null =
          option.item.badge === 'coming-soon'
            ? t('LABEL_BADGE_COMING_SOON')
            : option.item.badge === 'not-included'
              ? t('LABEL_BADGE_NOT_INCLUDED')
              : null;

        return (
          <Box
            key={key}
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...optionProps}
          >
            {option.item.icon && (
              <ListItemIcon
                sx={{ minWidth: 32, '& svg': { fontSize: '1.1rem' } }}
              >
                <option.item.icon fontSize="small" />
              </ListItemIcon>
            )}
            <ListItemText
              primary={<Typography variant="body2">{option.label}</Typography>}
              {...(badgeSubtext && {
                secondary: (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    {badgeSubtext}
                  </Typography>
                ),
              })}
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('SIDEBAR_SEARCH_PLACEHOLDER')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiAutocomplete-endAdornment': { display: 'none' },
          }}
        />
      )}
    />
  );
};
